import { isSentinelValue } from "@/lib/env";

const SENTRY_DISABLED_VALUES = new Set(["false", "0", "no", "off"]);

export const isSentryReportingEnabled = (): boolean => {
  const publicEnabled = process.env.NEXT_PUBLIC_SENTRY_ENABLED;
  const rawFlag = publicEnabled && !isSentinelValue(publicEnabled) ? publicEnabled : process.env.SENTRY_ENABLED;
  const flag = (rawFlag ?? "").trim().toLowerCase();
  return !SENTRY_DISABLED_VALUES.has(flag);
};

export const resolveSentryDsn = (): string | undefined => {
  const dsn = process.env.SENTRY_DSN ?? process.env.NEXT_PUBLIC_SENTRY_DSN;
  if (!dsn || isSentinelValue(dsn)) {
    return undefined;
  }
  return dsn;
};

export const isSentryEnabled = (): boolean => {
  if (!isSentryReportingEnabled()) {
    return false;
  }
  return Boolean(resolveSentryDsn());
};

export const parseSampleRate = (value: string | undefined, fallback: number): number => {
  const parsed = Number.parseFloat(value ?? "");
  return Number.isFinite(parsed) ? parsed : fallback;
};

const resolvePublicOrServerEnv = (publicKey: string, serverKey: string): string | undefined => {
  const publicValue = process.env[publicKey];
  const raw = publicValue && !isSentinelValue(publicValue) ? publicValue : process.env[serverKey];
  const trimmed = (raw ?? "").trim();
  return trimmed !== "" ? trimmed : undefined;
};

export const getSharedSentryOptions = () => {
  const publicEnvironment = process.env.NEXT_PUBLIC_SENTRY_ENVIRONMENT;
  const publicTracesSampleRate = process.env.NEXT_PUBLIC_SENTRY_TRACES_SAMPLE_RATE;
  const publicProfilesSampleRate = process.env.NEXT_PUBLIC_SENTRY_PROFILES_SAMPLE_RATE;
  const graphqlEndpoint = process.env.NEXT_PUBLIC_ADMIN_GRAPHQL_ENDPOINT;

  const tags: Record<string, string> = {
    api_surface: "admin",
  };

  const service = resolvePublicOrServerEnv("NEXT_PUBLIC_SENTRY_SERVICE", "SENTRY_SERVICE");
  if (service) {
    tags.service = service;
  }

  const imageTag =
    resolvePublicOrServerEnv("NEXT_PUBLIC_SENTRY_IMAGE_TAG", "SENTRY_IMAGE_TAG") ||
    resolvePublicOrServerEnv("NEXT_PUBLIC_SENTRY_RELEASE", "SENTRY_RELEASE");
  if (imageTag) {
    tags.image_tag = imageTag;
  }

  const tracePropagationTargets: Array<string | RegExp> = ["localhost", /^\/api\//];
  if (graphqlEndpoint && !isSentinelValue(graphqlEndpoint)) {
    tracePropagationTargets.push(graphqlEndpoint);
  }

  return {
    dsn: resolveSentryDsn(),
    enabled: isSentryEnabled(),
    environment:
      publicEnvironment && !isSentinelValue(publicEnvironment) ? publicEnvironment : process.env.SENTRY_ENVIRONMENT,
    release: process.env.NEXT_PUBLIC_SENTRY_RELEASE || process.env.SENTRY_RELEASE || undefined,
    sendDefaultPii: false,
    tracesSampleRate: parseSampleRate(
      publicTracesSampleRate && !isSentinelValue(publicTracesSampleRate)
        ? publicTracesSampleRate
        : process.env.SENTRY_TRACES_SAMPLE_RATE,
      0.1,
    ),
    profilesSampleRate: parseSampleRate(
      publicProfilesSampleRate && !isSentinelValue(publicProfilesSampleRate)
        ? publicProfilesSampleRate
        : process.env.SENTRY_PROFILES_SAMPLE_RATE,
      0.1,
    ),
    enableLogs: true,
    tracePropagationTargets,
    initialScope: { tags },
  };
};
